// ============================================================
// UserAvatar.tsx — a FIRM USER's avatar, resolved the ONE way everywhere
// (the Activity feed's rule): yourself → your authUser record, anyone else →
// the firm-users list (photo / colour / style), the name live via
// useLiveUserName (falling back to the name stored on the row). So the same
// person shows the same avatar in every feed, list, picker and header.
// `fallbackLook` — the colour / style / photo a row itself carries (a case's
// assignee, a presence viewer, an uploader) — is used ONLY when the person
// isn't in the firm list (removed, or the list not loaded yet); otherwise the
// account's current look always wins, so a stale row can't show an old face.
// Renders the shared <Avatar>. Users / staff only — never a client.
// ============================================================
import React from 'react'
import Avatar from './Avatar'
import { useCasevoStore } from '../../store/useCasevoStore'
import { useLiveUserName } from '../../hooks/useLiveUserName'

type AvatarSize = React.ComponentProps<typeof Avatar>['size']
export interface AvatarLook { colour?: string | null; style?: 'solid' | 'soft' | 'icon' | null; photoUrl?: string | null }

const UserAvatar: React.FC<{ userId: string | null | undefined; fallbackName: string; size?: AvatarSize; title?: boolean; fallbackLook?: AvatarLook }> = ({ userId, fallbackName, size = 'feed', title = true, fallbackLook }) => {
  const authUser = useCasevoStore((s) => s.authUser)
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const liveName = useLiveUserName()
  const name = liveName(userId, fallbackName) || fallbackName || 'Unknown'
  const u = userId && authUser?.id === userId ? authUser : firmUsers.find((f) => f.id === userId)
  const look: AvatarLook = u ? { colour: u.avatarColour, style: u.avatarStyle, photoUrl: u.avatarPhotoUrl ?? null } : (fallbackLook ?? {})
  const [first, ...rest] = name.split(' ')
  const face = <Avatar size={size} firstName={first ?? ''} lastName={rest.join(' ')} colour={look.colour ?? undefined} style={look.style ?? undefined} photoUrl={look.photoUrl ?? null} />
  return title ? <span className="inline-flex shrink-0" title={name}>{face}</span> : face
}

export default UserAvatar
